import { html } from 'lit';
import { sectionHeading } from '../components/sectionHeading.js';
import type { LandingContent } from '../../shared/content/landingContent.js';
import {
  editorNodeClass,
  editorNodeClick,
  editorNodePointerDown,
  editorNodeStyle,
  type LandingEditorRenderOptions,
} from '../editor/landingEditorRender.js';

const toggleTeamCard = (event: MouseEvent, editor?: LandingEditorRenderOptions, elementId?: string) => {
  if (editor?.mode === 'editor') {
    if (elementId) editorNodeClick(editor, elementId, event);
    return;
  }

  const card = event.currentTarget as HTMLElement;
  const willFlip = !card.classList.contains('is-flipped');
  card
    .closest('.vp-team-grid')
    ?.querySelectorAll<HTMLElement>('.vp-team-member.is-flipped')
    .forEach(other => {
      if (other === card) return;
      other.classList.remove('is-flipped');
      other.setAttribute('aria-pressed', 'false');
    });
  card.classList.toggle('is-flipped', willFlip);
  card.setAttribute('aria-pressed', String(willFlip));
};

const handleTeamCardKeydown = (event: KeyboardEvent, editor?: LandingEditorRenderOptions, elementId?: string) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  toggleTeamCard(event as unknown as MouseEvent, editor, elementId);
};

export const teamSection = (content: LandingContent, editor?: LandingEditorRenderOptions) => html`
  <section
    id="team"
    class=${editorNodeClass(editor, 'section.team', 'vp-section vp-team-section')}
    style=${editorNodeStyle(editor, 'section.team')}
    @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, 'section.team', event)}
    @click=${(event: MouseEvent) => editorNodeClick(editor, 'section.team', event)}
  >
    <div class="vp-shell">
      ${sectionHeading({
        eyebrow: content.team.eyebrow,
        title: content.team.title,
        highlight: content.team.titleHighlight,
        text: content.team.text,
        editor,
        elementPrefix: 'team',
      })}
      <div
        class=${editorNodeClass(editor, 'team.grid', 'vp-team-grid')}
        style=${editorNodeStyle(editor, 'team.grid')}
        @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, 'team.grid', event)}
        @click=${(event: MouseEvent) => editorNodeClick(editor, 'team.grid', event)}
      >
        ${content.team.members.map((member, index) => {
          const memberId = `team.member.${index}`;
          const hasBackContent = Boolean(member.story || member.motivation);
          return html`
            <article
              class=${editorNodeClass(
                editor,
                memberId,
                `vp-team-member reveal-card${hasBackContent ? ' vp-team-member-flippable' : ''}`,
              )}
              style=${editorNodeStyle(editor, memberId)}
              tabindex="0"
              role=${hasBackContent ? 'button' : 'article'}
              aria-pressed=${hasBackContent ? 'false' : undefined}
              @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, memberId, event)}
              @click=${(event: MouseEvent) =>
                hasBackContent ? toggleTeamCard(event, editor, memberId) : editorNodeClick(editor, memberId, event)}
              @keydown=${(event: KeyboardEvent) =>
                hasBackContent ? handleTeamCardKeydown(event, editor, memberId) : undefined}
            >
              <div class="vp-team-member-inner">
                <div class="vp-team-member-face vp-team-member-face-front">
                  <div class="vp-team-member-media">
                    <img
                      class=${editorNodeClass(editor, `${memberId}.image`)}
                      style=${editorNodeStyle(editor, `${memberId}.image`)}
                      src=${member.image}
                      alt=${member.name}
                      loading="lazy"
                      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${memberId}.image`, event)}
                      @click=${(event: MouseEvent) => editorNodeClick(editor, `${memberId}.image`, event)}
                    />
                    <span class="vp-team-member-role-pill">${member.role}</span>
                    ${hasBackContent
                      ? html`
                          <span class="vp-team-flip-cue" aria-hidden="true">
                            <span class="icon-[material-symbols--arrow-forward-rounded]"></span>
                          </span>
                        `
                      : ''}
                  </div>
                  <div class="vp-team-member-body">
                    <h3
                      class=${editorNodeClass(editor, `${memberId}.name`)}
                      style=${editorNodeStyle(editor, `${memberId}.name`)}
                      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${memberId}.name`, event)}
                      @click=${(event: MouseEvent) => editorNodeClick(editor, `${memberId}.name`, event)}
                    >
                      ${member.name}
                    </h3>
                    <span
                      class=${editorNodeClass(editor, `${memberId}.role`, 'vp-team-member-role')}
                      style=${editorNodeStyle(editor, `${memberId}.role`)}
                      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${memberId}.role`, event)}
                      @click=${(event: MouseEvent) => editorNodeClick(editor, `${memberId}.role`, event)}
                      >${member.role}</span
                    >
                    <p
                      class=${editorNodeClass(editor, `${memberId}.text`)}
                      style=${editorNodeStyle(editor, `${memberId}.text`)}
                      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${memberId}.text`, event)}
                      @click=${(event: MouseEvent) => editorNodeClick(editor, `${memberId}.text`, event)}
                    >
                      ${member.text}
                    </p>
                  </div>
                </div>
                ${hasBackContent || editor
                  ? html`
                      <div class="vp-team-member-face vp-team-member-face-back">
                        <div class="vp-team-member-back-scroll">
                          <span class="vp-team-member-story-label"
                            >${content.locale === 'tr' ? 'Hikâye' : 'Story'}</span
                          >
                          <p
                            class=${editorNodeClass(editor, `${memberId}.story`)}
                            style=${editorNodeStyle(editor, `${memberId}.story`)}
                            @pointerdown=${(event: PointerEvent) =>
                              editorNodePointerDown(editor, `${memberId}.story`, event)}
                            @click=${(event: MouseEvent) => editorNodeClick(editor, `${memberId}.story`, event)}
                          >
                            ${member.story || member.text}
                          </p>
                          <span class="vp-team-member-story-label"
                            >${content.locale === 'tr' ? 'Motivasyon' : 'Motivation'}</span
                          >
                          <p
                            class=${editorNodeClass(editor, `${memberId}.motivation`)}
                            style=${editorNodeStyle(editor, `${memberId}.motivation`)}
                            @pointerdown=${(event: PointerEvent) =>
                              editorNodePointerDown(editor, `${memberId}.motivation`, event)}
                            @click=${(event: MouseEvent) => editorNodeClick(editor, `${memberId}.motivation`, event)}
                          >
                            ${member.motivation || ''}
                          </p>
                        </div>
                      </div>
                    `
                  : ''}
              </div>
            </article>
          `;
        })}
      </div>
    </div>
  </section>
`;
